import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import FormWrapper from "@/components/common/FormWrapper";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { useState, type ReactNode } from "react";
import { useDebounceValue } from "usehooks-ts";
import { useGetTeamsInfiniteQuery } from "@/services/(company)/api.teams";
import { Spinner } from "@/components/ui/v2/spinner";
import { Loader as LoaderIcon, Search } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { startCase } from "lodash";
import { Checkbox } from "@/components/ui/checkbox";
import { useOnInView } from "react-intersection-observer";
import { RightBlock } from "./right-block";
import { useGetProjectTeamsByProjectIdQuery } from "@/services/(company)/api.projects";

import * as yup from "yup";
import { useFormik } from "formik";

import { useAssignProjectTeamsMutation } from "@/services/(company)/api.projects";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import type { projectIdKey } from "@/routes/company/routes.projects.constants";
import { usePermissions } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { AccessDenied } from "@/components/permission-guard";

export interface ITeamDisplayData {
  id: number;
  name: string | null;
  members: {
    name: string | null;
    profileImage: string | null;
    roleName: string | null;
  }[];
}

const buildFormSchema = (t: TFunction) =>
  yup.object({
    teamIds: yup
      .array()
      .of(
        yup.object({
          id: yup.number().required(),
          // Storing extra UI data (name, avatar) here for the RightBlock to render without searching the team list.
          name: yup.string().nullable(),
          members: yup
            .array()
            .of(
              yup.object({
                name: yup.string().nullable(),
                profileImage: yup.string().nullable(),
                roleName: yup.string().nullable(),
              }),
            )
            .nullable(),
        }),
      )
      .min(1, t("projects:select_at_least_one_team"))
      .required(t("projects:team_selection_is_required")),
  });
type Fields = yup.InferType<ReturnType<typeof buildFormSchema>>;

export const AddProjectTeamForm = () => {
  const { t } = useTranslation(["projects", "common"]);
  const navigate = useNavigate();
  const { projectId } = useParams<{ [projectIdKey]: string }>();
  const { can, isLoading: isPermissionsLoading } = usePermissions();
  const [assignTeams, { isLoading: isAssigning }] =
    useAssignProjectTeamsMutation();

  const { data: { data: assignedTeams } = {}, isLoading } =
    useGetProjectTeamsByProjectIdQuery(
      { projectId: projectId! },
      { skip: !projectId, refetchOnMountOrArgChange: true },
    );

  const formik = useFormik<Fields>({
    enableReinitialize: true,
    initialValues: {
      teamIds:
        assignedTeams?.map((team) => ({
          id: team.id,
          name: team.name,
          members: team?.members.map((member) => ({
            name: member.user?.fullName,
            profileImage: member?.user?.profileImage,
            roleName: member?.role?.roleName ?? t("projects:no_roles_assigned"),
          })),
        })) ?? [],
    },
    validationSchema: buildFormSchema(t),
    onSubmit: async ({ teamIds }) => {
      if (!projectId) return toast.error(t("projects:project_no_found"));
      await assignTeams({
        projectId,
        teamIds: teamIds.map((team) => team.id),
      })
        .unwrap()
        .then((res) => {
          navigate(-1);
          toast.success(res.message);
        })
        .catch((e) => toast.error(e?.data?.message ?? e?.message));
    },
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebounceValue(searchQuery, 500);

  const {
    data: teamsResp,
    isFetching,
    isLoading: isLoadingTeams,
    fetchNextPage: fetchNextPage,
    isFetchingNextPage: isFetchingNextPage,
  } = useGetTeamsInfiniteQuery({ limit: 10, search: debouncedSearch });
  const teams = teamsResp?.pages.flatMap((page) => page.data) ?? [];

  const infiniteScrollAnchor = useOnInView((inView) => {
    if (inView) fetchNextPage();
  });

  const getTeamIndex = (id: number) =>
    (formik.values.teamIds ?? []).findIndex((team: any) => team.id === id);

  if (isPermissionsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoaderIcon className="animate-spin text-primary" />
      </div>
    );
  }

  if (!can(PLATFORM_SECTION_ENUM.PROJECTS, "edit")) {
    return (
      <AccessDenied
        description={t(
          "projects:you_don_t_have_permission_to_manage_project_teams",
        )}
        onBack={() => navigate(-1)}
      />
    );
  }

  const toggleTeamSelection = (teamId: number) => {
    const currentTeams = formik.values.teamIds ?? [];
    const isExisting = currentTeams.some((team) => team.id === teamId);

    if (isExisting) {
      const updatedTeams = currentTeams.filter((team) => team.id !== teamId);
      formik.setFieldValue("teamIds", updatedTeams);
    } else {
      const team = teams.find((it) => it.id === teamId);
      if (team) {
        const newTeamEntry = {
          id: team.id,
          name: team.name,
          members:
            team.members?.map((member) => ({
              name: member.user?.fullName ?? t("projects:unknown"),
              profileImage: member.user?.profileImage ?? null,
              roleName: member.role?.roleName ?? t("projects:no_role_assigned"),
            })) || [],
        };
        formik.setFieldValue("teamIds", [...currentTeams, newTeamEntry]);
      }
    }
  };

  return (
    <FormWrapper
      title={t("projects:add_team_for_project")}
      subtitle={t(
        "projects:assign_teams_to_align_responsibility_and_performace_for_",
      )}
      submitText={t("projects:save")}
      onSubmit={formik.handleSubmit}
      loading={isAssigning}
    >
      <div className="flex flex-wrap gap-4">
        <aside className="h-144 lg:h-152 w-full min-w-80 lg:w-1/4 flex flex-col min-h-0 overflow-hidden border border-primary rounded-sm">
          <BlockHeader>
            <InputGroup className="bg-white">
              <InputGroupInput
                value={searchQuery}
                placeholder={t("projects:search_teams")}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <InputGroupAddon>
                {isFetching ? (
                  <Spinner size="sm" />
                ) : (
                  <Search className="opacity-80" />
                )}
              </InputGroupAddon>
            </InputGroup>
          </BlockHeader>

          {/* //// Teams listing //// */}
          {isLoading ? (
            <Spinner />
          ) : (
            <div className="flex-1 overflow-y-auto flex flex-col gap-2 p-2 sm:p-4">
              {isLoadingTeams ? (
                <Spinner />
              ) : teams.length === 0 ? (
                <small>{t("projects:no_teams_found")}</small>
              ) : (
                teams.map((team) => {
                  const isSelected = getTeamIndex(team.id) > -1;
                  return (
                    <div
                      key={team.id}
                      className="flex items-center justify-between px-2 sm:px-3 py-2 border rounded-sm hover:bg-primary/10 relative gap-2"
                    >
                      <div
                        className="flex gap-2 items-center cursor-pointer min-w-0 flex-1"
                        onClick={() => toggleTeamSelection(team.id)}
                      >
                        <Avatar className="shrink-0 h-8 w-8 sm:h-10 sm:w-10">
                          <AvatarImage src={team.image ?? undefined} />
                          <AvatarFallback className="text-xs sm:text-sm">
                            {team.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col min-w-0 flex-1">
                          <p className="truncate text-sm sm:text-base leading-tight">
                            {startCase(team.name)}
                          </p>
                        </div>
                      </div>
                      <Checkbox
                        className="shrink-0"
                        checked={isSelected}
                        onCheckedChange={() => toggleTeamSelection(team.id)}
                      />
                    </div>
                  );
                })
              )}
              <div ref={infiniteScrollAnchor} />
              {isFetchingNextPage && <Spinner wrapperClassName="h-5" />}
            </div>
          )}
        </aside>

        <RightBlock
          teams={formik.values.teamIds as unknown as ITeamDisplayData[]}
        />
      </div>
    </FormWrapper>
  );
};

export const BlockHeader = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={`bg-primary/10 border-b flex-none px-4 h-14 flex items-center ${className}`}
    >
      {children}
    </div>
  );
};
